'use strict';
import DataModelProfiler from './data-model-profiler';
import { DataModelSystemProperty, DataModelCalculatedProperty } from './data-model-calculated-property';
import { Relation, DataModelMultiFragmentArray } from './data-model-base-objects';
import '../enumerations';
import '../../../Pandora/model/enumerations';

export default function DataModelHelper() {
	if (DataModelHelper.prototype._instance) {
		return DataModelHelper.prototype._instance;
	}

	const additionalData = { config: {}, userProfile: {} };
	const sysProperties = DataModelSystemProperty;

	const service = {
		additionalData: additionalData,
		preparePath: preparePath,
		applySourceResult: applySourceResult,
		createModelDefaultObject: createModelDefaultObject,
		updateDirections: updateDirections,
		applyCalculatedProperties: applyCalculatedProperties,
		initRelations: initRelations,
		profiler: new DataModelProfiler(),
	};

	DataModelHelper.prototype._instance = service;
	return DataModelHelper.prototype._instance;

	async function applyCalculatedProperties(record, properties) {
		const calcProperties = [];
		const sortedProperties = sortByPath(properties || []);

		sortedProperties.forEach(function (property) {
			if (!property.Path) {
				property.Path = property.ParentPath + '.' + property.Name;
			}
			calcProperties.push(new DataModelCalculatedProperty(property).register(record));
		});

		for (const property of calcProperties) {
			property.apply(record, additionalData);
			if (property.promiseToWaitBeforeProseed) {
				await property.promiseToWaitBeforeProseed;
				delete property.promiseToWaitBeforeProseed;
			}
		}

		function sortByPath(properties) {
			const sortedProperties = [];
			// fields = fields.slice();
			let field;
			for (field = properties[0]; field; field = properties[0]) {
				push(properties, sortedProperties, field.Path);
			}
			return sortedProperties;

			function indexOf(properties, path) {
				let i = 0;
				const len = properties.length;
				for (; i < len; i++) {
					if (properties[i].Path === path) {
						return i;
					}
				}
				return -1;
			}

			function push(properties, sortedProperties, path) {
				const index = indexOf(properties, path);
				if (index !== -1) {
					const property = properties.splice(index, 1)[0];
					property.Parameters.forEach(function (param) {
						push(properties, sortedProperties, param.path);
					});
					sortedProperties.push(property);
				}
			}
		}
	}

	function updateDirections(sources, properties) {
		properties.forEach(function (propertyDescriptor) {
			const path = propertyDescriptor.Path;
			let property;
			sources.some(function (source) {
				if (path.indexOf(source.path) === 0) {
					property = source.find(path);
					if (property) {
						return true;
					}
				} else if (source.path === path) {
					property = source;
					return true;
				}
			});
			if (property) {
				propertyDescriptor.Direction = property.direction;
			}
		});
	}

	function applySourceResult(model, newData, source, response) {
		const modelSourceData = model[source.path];
		let sourceReturnPath = '';
		const calcProperties = model.$calcProperties;

		if (source.dataDefinition) {
			newData = newData && newData.Entity;
			if (newData) {
				if (!source.isArray && Array.isArray(newData[source.dataDefinition])) {
					const result = newData[source.dataDefinition];
					newData[source.dataDefinition] = result && result[0];
				}
				const sysPropertiesNames = getPropertiesNames(sysProperties);
				_.forOwn(newData, function (value, prop) {
					if (prop !== source.dataDefinition && sysPropertiesNames.indexOf(prop) === -1) {
						modelSourceData[prop] = newData[prop];
					}
				});

				extendData(modelSourceData, source.dataDefinition, newData[source.dataDefinition], source.path + '.' + source.dataDefinition);
				modelSourceData[sysProperties.TotalCount] = Number(response.headers('TotalCount') || '');
			}
		} else if (source.entityName) {
			sourceReturnPath = source.path + '.' + source.entityName;
			newData = newData && newData.Entity ? newData.Entity : newData;
			extendData(modelSourceData, source.entityName, newData, sourceReturnPath);
		} else {
			sourceReturnPath = source.path + '.' + sysProperties.Return;
			extendData(modelSourceData, sysProperties.Return, newData, sourceReturnPath);

			if (source.dataQueryId && Object.prototype.hasOwnProperty.call(modelSourceData, sysProperties.First)) {
				modelSourceData[sysProperties.TotalCount] = Number(response.headers('TotalCount') || '');
				modelSourceData[sysProperties.First] = modelSourceData[sysProperties.Return][0];
			}
		}

		service.profiler.sourceExecuted(source.name, newData, modelSourceData);

		function getPropertiesNames(obj) {
			const names = [];
			_.forOwn(obj, function (val) {
				names.push(val);
			});
			return names;
		}

		function extendData(parent, property, data, applyPath) {
			initRelations(data, parent[property]);

			if (!Array.isArray(data) && ((source.rewriteResponse && typeof data === 'object') || data === null || data === undefined)) {
				const defaultObj = createModelDefaultObject([source]);

				data = merge(defaultObj[source.path][property], data, function (value) {
					return !(value instanceof Relation);
				});
			}
			calcProperties.$$merge(parent, property, data, applyPath);
		}
	}
	function merge(defaultValue, rawData, goDeeper) {
		if (isMergable(rawData) && isMergable(defaultValue)) {
			Object.keys(rawData).forEach(function (key) {
				if (isMergable(rawData[key]) && isMergable(defaultValue[key]) && goDeeper(rawData[key])) {
					merge(defaultValue[key], rawData[key], goDeeper);
				} else {
					defaultValue[key] = rawData[key];
				}
			});

			return defaultValue;
		}

		return rawData || defaultValue;
	}

	function isMergable(value) {
		return typeof value === 'object' && !Array.isArray(value) && value !== null && typeof value !== 'undefined';
	}

	function createModelDefaultObject(sources, defaultData) {
		const result = {};

		sources.forEach(function (source) {
			result[source.path] = {};
			result[source.path][DataModelSystemProperty.SourceState] = mx.SolutionBuilderAgent.SourceLoadingStatus.INITIAL;
			source.items.forEach(function (item) {
				_initValue(result[source.path], item);
			});
		});
		return result;

		function _initValue(obj, sourceItem) {
			const defaultValue = getDefaultObjectByType(defaultData, sourceItem);

			if (sourceItem.items && !sourceItem.isArray) {
				sourceItem.items.forEach(function (item) {
					_initValue(defaultValue, item);
				});
			}

			if (sourceItem.isArray) {
				defaultValue.getDefaultItem = function () {
					const defaultItem = {};
					(sourceItem.items || []).forEach(function (item) {
						_initValue(defaultItem, item);
					});
					return defaultItem;
				};
			}

			obj[sourceItem.name] = defaultValue;
		}
	}

	function initRelations(entityProp, defaultModelProp, parentArray) {
		if (!defaultModelProp || !entityProp) {
			return;
		}

		if (Array.isArray(entityProp) && typeof defaultModelProp.getDefaultItem === 'function') {
			entityProp.forEach(function (item) {
				initRelations(item, defaultModelProp.getDefaultItem(), entityProp);
			});

			entityProp.getDefaultItem = defaultModelProp.getDefaultItem;
			return;
		}

		Object.keys(defaultModelProp).forEach(function (key) {
			if (defaultModelProp[key] instanceof Relation) {
				defaultModelProp[key].set(entityProp[key], { fragmentId: entityProp.ID });
				entityProp[key] = defaultModelProp[key];
			} else if (defaultModelProp[key] instanceof DataModelMultiFragmentArray) {
				if (Array.isArray(entityProp[key])) {
					defaultModelProp[key].set(entityProp[key], { objectId: entityProp._id });
					entityProp[key] = defaultModelProp[key];

					if (defaultModelProp[key] && defaultModelProp[key].getDefaultItem) {
						entityProp[key].forEach(function (item) {
							initRelations(item, defaultModelProp[key].getDefaultItem());
						});
						entityProp[key].getDefaultItem = defaultModelProp[key].getDefaultItem;
					}
				}
			} else if (typeof defaultModelProp[key] === 'object' && defaultModelProp[key] !== null) {
				if (typeof entityProp[key] === 'object' && entityProp[key] !== null) {
					initRelations(entityProp[key], defaultModelProp[key]);
				} else if (parentArray) {
					const entityPropObject = {};
					initRelations(entityPropObject, defaultModelProp[key]);

					if (Object.keys(entityPropObject).length) {
						entityProp[key] = entityPropObject;
					}
				}
			}
		});
	}

	function getDefaultArray(defaultData, sourceItem) {
		if (sourceItem.isMultiFragment) {
			return defaultData instanceof DataModelMultiFragmentArray
				? defaultData
				: new DataModelMultiFragmentArray(defaultData, {
						entityClassName: sourceItem.name,
						entityType: sourceItem.entityName,
						sourceId: sourceItem.sourceId,
					});
		} else {
			return Array.isArray(defaultData) ? defaultData : [];
		}
	}

	function getDefaultObjectByType(/* type, isArray, path, relationsConfig, */ defaultData, sourceItem) {
		const defaultValue = defaultData ? _.get(defaultData, sourceItem.path) : sourceItem.defaultValue ?? null;
		const type = sourceItem.type;
		const relationsConfig = sourceItem.relationsConfig;

		if (sourceItem.isArray) {
			return getDefaultArray(defaultData, sourceItem);
		}
		//TODO: After thinking TODO section below and finding solution remove comments, VirtualRelationEnd section and this line
		// if (relationsConfig) {
		// 	return relationsConfig.createRelation();
		// }

		switch (type) {
			case 'IntType':
			case 'DecimalType':
				return angular.isNumber(defaultValue) ? defaultValue : 0;
			case 'DateType':
				if (angular.isDate(defaultValue)) {
					return defaultValue.toISOString();
				}

				if (angular.isString(defaultValue) && angular.isDate(new Date(defaultValue))) {
					return new Date(defaultValue).toISOString();
				}
				return null;
			case 'GuidType':
			case 'StringType':
			case 'TextType':
				return angular.isString(defaultValue) ? defaultValue : null;
			case 'BoolType':
				return defaultValue === true;
			case 'Currency':
				if (angular.isObject(defaultValue)) {
					return angular.extend({}, defaultValue);
				}
				return {};
			case 'VirtualRelationEnd':
				if (relationsConfig.otherSideIsMany()) {
					return relationsConfig.createRelation();
				}
				//TODO: Think about using relationConfig in ObjectPickers
				return angular.isString(defaultValue) ? defaultValue : null;
			default:
				return {};
		}
	}

	function preparePath(path, data, source, extOptions) {
		extOptions = extOptions || {};
		const options = {
			useQueryObject: true,
			skipEmptyParameters: false,
		};

		angular.extend(options, extOptions);

		const result = {
			path: path,
			queryObjectString: '',
			queryObject: {},
			execute: true,
		};

		let queryObject;

		const split = path
			.replace('?}', '_#######_')
			.split('?')
			.map(function (v) {
				return v.replace('_#######_', '?}');
			});

		if (split.length > 2) {
			result.execute = false;
		} else {
			const pathPart = split[0];
			const queryString = split[1] || '';

			result.path = replace(pathPart, data, source);
			queryObject = getQueryObject(queryString, data, source);
		}

		if (!options.useQueryObject && queryObject) {
			let queryObjectString = '';

			Object.keys(queryObject).forEach(function (key) {
				let val = queryObject[key];

				if (options.skipEmptyParameters && (typeof val === 'undefined' || val === null)) {
					return;
				}

				if (queryObjectString) {
					queryObjectString += '&';
				}
				if (typeof val === 'undefined' || val === null) {
					val = '';
				}
				if (Array.isArray(val)) {
					val = val.join();
				}
				queryObjectString += key + '=' + val;
			});

			if (queryObjectString) {
				result.queryObjectString = queryObjectString;
				result.path += '?' + queryObjectString;
			}
		} else {
			result.queryObject = queryObject;
		}

		return result;

		function getQueryObject(queryString, data, source) {
			const obj = {};

			if (queryString) {
				// Fill queryObject with properties used in query string template
				queryString.split('&').forEach(function (n) {
					const param = n.split('=');

					// convert array params correctly
					param[0] = param[0].replace('[0]', '').replace('[1]', '');
					param[1] = param[1].replace('[0]', '').replace('[1]', '');

					if (/{([^:}]+)(:?:[^}]+)?}/g.test(param[1])) {
						const value = getValue(
							param[1].replace(/{([^:}]+)(:?:[^}]+)?}/g, function (match, param) {
								return param;
							}),
							data,
							source
						);

						if (typeof value === 'undefined') {
							result.execute = false;
						}
						obj[param[0]] = value;
					} else {
						obj[param[0]] = param[1];
					}
				});
			} else if (source && source.httpMethod === mx.shell.HttpMethod.Get) {
				// Fill queryObject with all source data properties (except internal)
				const keys = Object.keys(data);

				for (let i = 0, len = keys.length; i < len; i++) {
					const key = keys[i];
					const prop = data[key];
					// TODO: Create uniform rule to get only data properties.
					// Avoid having '' (empty string) in unassigned data.
					if (
						key !== 'return' &&
						key !== 'undefined' &&
						key.charAt(0) !== '$' &&
						typeof prop !== 'function' &&
						typeof prop !== 'undefined' &&
						!(prop === null || prop === '')
					) {
						obj[key] = prop;
					}
				}
			}

			return obj;
		}

		function replace(path, data, source) {
			let replaceResult = path.replace(/{([^:}]+)(:?:[^}]+)?}/g, function (match, param) {
				let value = getValue(param, data, source);
				if (typeof value === 'undefined' || value === null) {
					result.execute = false;
				}
				return value === null ? '' : value;
			});
			// replace consequent slashes ('/') with single one, remove trailing slash
			return replaceResult.replace(/\/+/g, '/').replace(/\/+$/, '');
		}

		function getValueSafe(data, property) {
			if (Object.prototype.hasOwnProperty.call(data, property)) {
				return data[property];
			}

			if (typeof property === 'string' && property.length) {
				let key = property[0];
				key = key === key.toUpperCase() ? key.toLowerCase() : key.toUpperCase();

				key += property.substr(1);
				return data[key];
			}
		}

		function getValue(property, data, source) {
			//try get value with a key or with a key that has case-inverted first letter
			let value = getValueSafe(data, property);

			if (value === null && source) {
				// Exec with default value
				const items = source.items.filter(function (item) {
					return item.name === property;
				});

				if (items.length === 1) {
					value = getDefaultValue(items[0].type, items[0].isArray);
				} else {
					value = undefined;
				}
			}
			return value;
		}

		function getDefaultValue(type, isArray) {
			if (isArray) {
				return [];
			}
			switch (type) {
				case 'IntType':
					return 0;
				case 'DateType':
					return null;
				case 'GuidType':
				case 'StringType':
					return null;
				case 'BoolType':
					return false;
				default:
					return {};
			}
		}
	}
}
